import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  TextField,
  ThemeProvider as MuiThemeProvider,
  Typography,
} from '@mui/material';
import { Language, Visibility, VisibilityOff, ArrowForward, CheckRounded } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { createAppTheme } from '../../theme';
import { landing, landingType, palbonLandingConfig, glassSurface, gradientText, reducedMotion } from '../landing/landingTokens';
import { ROOT_DOMAIN, getSubdomain } from '../../utils/subdomain';
import Wordmark from '../../components/common/Wordmark';

/** The connected business systems shown as live status indicators. */
const MODULES = ['Finance', 'Supply Chain', 'Sales', 'Production', 'Projects'];

/**
 * A very restrained ambient background: a fine grid, two slow light glows, a
 * small constellation of nodes + connection lines with occasional data pulses.
 * Deliberately near-imperceptible; intensifies slightly when `active`.
 */
const NetworkBackdrop: React.FC<{ active: boolean }> = ({ active }) => {
  const nodes = useMemo(
    () => [
      { x: 14, y: 22 }, { x: 30, y: 62 }, { x: 8, y: 78 }, { x: 44, y: 34 },
      { x: 66, y: 18 }, { x: 82, y: 46 }, { x: 90, y: 78 }, { x: 60, y: 82 },
      { x: 72, y: 62 }, { x: 24, y: 44 },
    ],
    []
  );
  const links = useMemo(
    () => [[0, 9], [9, 1], [1, 2], [9, 3], [3, 4], [4, 5], [5, 8], [8, 6], [8, 7], [1, 7], [3, 5]],
    []
  );

  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        opacity: active ? 1 : 0.82,
        transition: 'opacity .6s ease',
        // grid
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          backgroundImage: `linear-gradient(${landing.gridLine} 1px, transparent 1px), linear-gradient(90deg, ${landing.gridLine} 1px, transparent 1px)`,
          backgroundSize: '56px 56px',
          maskImage: 'radial-gradient(ellipse at 50% 40%, #000 30%, transparent 82%)',
          WebkitMaskImage: 'radial-gradient(ellipse at 50% 40%, #000 30%, transparent 82%)',
        },
        // ambient glows
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: 0,
          background: [
            `radial-gradient(900px 500px at 20% 20%, rgba(56,189,248,${active ? 0.14 : 0.09}) 0%, transparent 60%)`,
            `radial-gradient(800px 520px at 82% 78%, rgba(167,139,250,${active ? 0.12 : 0.08}) 0%, transparent 60%)`,
          ].join(', '),
          transition: 'background .6s ease',
        },
        '@keyframes nodePulse': { '0%,100%': { opacity: 0.4 }, '50%': { opacity: 1 } },
        '@keyframes dash': { to: { strokeDashoffset: 0 } },
        '@keyframes flow': { '0%': { offsetDistance: '0%', opacity: 0 }, '15%': { opacity: 1 }, '85%': { opacity: 1 }, '100%': { offsetDistance: '100%', opacity: 0 } },
      }}
    >
      {/* connection lines (a stretched viewBox is fine for faint dashes) */}
      <Box component="svg" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} preserveAspectRatio="none" viewBox="0 0 100 100">
        {links.map(([a, b], i) => (
          <line
            key={i}
            x1={nodes[a].x} y1={nodes[a].y} x2={nodes[b].x} y2={nodes[b].y}
            stroke="rgba(129,140,248,0.16)" strokeWidth={0.18}
            strokeDasharray="1.5 1.5"
            style={{ strokeDashoffset: 6, animation: 'dash 6s linear infinite' }}
          />
        ))}
      </Box>
      {/* nodes as true round dots (percentage-positioned so they never distort) */}
      {nodes.map((n, i) => (
        <Box
          key={`nd${i}`}
          sx={{
            position: 'absolute',
            left: `${n.x}%`,
            top: `${n.y}%`,
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: '#38bdf8',
            boxShadow: '0 0 10px rgba(56,189,248,0.7)',
            transform: 'translate(-50%,-50%)',
            transformOrigin: 'center',
            animation: `nodePulse ${3 + (i % 4)}s ease-in-out ${i * 0.35}s infinite`,
          }}
        />
      ))}
    </Box>
  );
};

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const LAUNCH_MODULES = ['Finance', 'Procurement', 'Inventory', 'Sales', 'Production', 'Projects'];

/**
 * Post-authentication transition. Credentials are already accepted; this plays
 * a short "business systems coming online" sequence — a filling progress ring
 * around the Palbon mark and modules checking in — then hands off to the app.
 */
const LaunchOverlay: React.FC<{ phase: 'authing' | 'ready' }> = ({ phase }) => {
  const C = 2 * Math.PI * 54;
  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 4000,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 4,
        backgroundColor: landing.ink,
        background: landing.heroGradient,
        color: landing.textOnDark,
        animation: 'ovIn .35s ease',
        '@keyframes ovIn': { from: { opacity: 0 }, to: { opacity: 1 } },
        '@keyframes ringFill': { from: { strokeDashoffset: C }, to: { strokeDashoffset: 0 } },
        '@keyframes chipOn': { from: { opacity: 0.35, transform: 'translateY(4px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
        '@keyframes coreUp': { from: { transform: 'scale(0.9)', opacity: 0 }, to: { transform: 'scale(1)', opacity: 1 } },
        '@keyframes fadeUp': { from: { opacity: 0, transform: 'translateY(6px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      {/* Ring + mark */}
      <Box sx={{ position: 'relative', width: 148, height: 148, animation: 'coreUp .4s ease', '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }}>
        <Box component="svg" viewBox="0 0 120 120" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
          <circle cx={60} cy={60} r={54} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={3} />
          <circle
            cx={60} cy={60} r={54} fill="none" stroke="#38bdf8" strokeWidth={3} strokeLinecap="round"
            strokeDasharray={C}
            style={{ strokeDashoffset: 0, animation: 'ringFill 2s cubic-bezier(.16,1,.3,1) forwards' }}
          />
        </Box>
        <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
          <Wordmark size={46} tone="dark" gradient showText={false} />
        </Box>
      </Box>

      {/* Status */}
      <Box sx={{ textAlign: 'center' }}>
        {phase === 'ready' ? (
          <>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, animation: 'fadeUp .35s ease' }}>
              <CheckRounded sx={{ fontSize: 22, color: '#22c55e' }} />
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, letterSpacing: '0.06em', ...gradientText }}>PALBON ONLINE</Typography>
            </Box>
            <Typography sx={{ mt: 1, fontSize: '0.9375rem', color: landing.textOnDarkMuted, animation: 'fadeUp .4s ease' }}>Your business is ready.</Typography>
          </>
        ) : (
          <Typography sx={{ fontSize: '1.0625rem', fontWeight: 700, letterSpacing: '0.16em', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: landing.accent }}>
            AUTHENTICATING…
          </Typography>
        )}
      </Box>

      {/* modules coming online */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 1, maxWidth: 420, px: 2 }}>
        {LAUNCH_MODULES.map((m, i) => (
          <Box
            key={m}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.5, borderRadius: 999,
              border: '1px solid rgba(34,197,94,0.3)', backgroundColor: 'rgba(34,197,94,0.08)',
              animation: `chipOn .4s ease ${0.15 + i * 0.24}s both`,
              '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 1 },
            }}
          >
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#22c55e' }} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: landing.textOnDark }}>{m}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(true);
  const [active, setActive] = useState(false);
  const [phase, setPhase] = useState<'idle' | 'authing' | 'ready'>('idle');
  const { login, isLoading, error } = useAuth();

  const authTheme = useMemo(() => createAppTheme(palbonLandingConfig), []);
  const workspace = getSubdomain();
  const ready = Boolean(email && password);

  const system = isLoading
    ? { label: 'AUTHENTICATING', color: landing.accent, glow: true }
    : ready
    ? { label: 'SYSTEM READY', color: '#22c55e', glow: false }
    : { label: 'SYSTEM STANDBY', color: '#f59e0b', glow: false };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login(email, password, async () => {
        // credentials accepted — play the launch transition (~2s) before handoff
        setPhase('authing');
        await sleep(1250);
        setPhase('ready');
        await sleep(800);
      });
      // auth state now flips → the router transitions to the dashboard.
    } catch {
      setPhase('idle'); // wrong password fails fast, before the overlay
    }
  };

  const darkField = {
    mb: 2.25,
    '& .MuiInputLabel-root': { color: 'rgba(226,232,240,0.6)' },
    '& .MuiInputLabel-root.Mui-focused': { color: landing.accent },
    '& .MuiOutlinedInput-root': {
      color: landing.textOnDark,
      backgroundColor: 'rgba(255,255,255,0.035)',
      borderRadius: 2.5,
      transition: 'box-shadow .2s, background-color .2s',
      '& fieldset': { borderColor: 'rgba(255,255,255,0.14)', transition: 'border-color .2s' },
      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.28)' },
      '&.Mui-focused': { backgroundColor: 'rgba(56,189,248,0.06)', boxShadow: '0 0 0 3px rgba(56,189,248,0.16)' },
      '&.Mui-focused fieldset': { borderColor: landing.accent },
    },
    '& input:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0 100px #0a1024 inset',
      WebkitTextFillColor: landing.textOnDark,
      caretColor: landing.textOnDark,
      borderRadius: 'inherit',
    },
    '& .MuiInputAdornment-root .MuiIconButton-root': { color: 'rgba(226,232,240,0.6)' },
  } as const;

  return (
    <MuiThemeProvider theme={authTheme}>
      {phase !== 'idle' && <LaunchOverlay phase={phase === 'ready' ? 'ready' : 'authing'} />}
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          overflowX: 'hidden',
          overflowY: 'auto',
          backgroundColor: landing.ink,
          background: landing.heroGradient,
          color: landing.textOnDark,
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'center',
          px: { xs: 2, sm: 4 },
          py: { xs: 4, md: 6 },
        }}
      >
        <NetworkBackdrop active={active} />

        {/* Composition */}
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            width: '100%',
            minWidth: 0,
            maxWidth: 1120,
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1.05fr) minmax(0, 0.95fr)' },
            alignItems: 'center',
            columnGap: { md: 10 },
            rowGap: 5,
          }}
        >
          {/* LEFT — brand story */}
          <Box sx={{ display: { xs: 'none', md: 'block' }, maxWidth: 520 }}>
            <Wordmark size={40} tone="dark" gradient />
            <Typography sx={{ ...landingType.eyebrow, color: 'rgba(226,232,240,0.55)', mt: 5, mb: 2.5 }}>
              Enterprise Business OS
            </Typography>
            <Typography component="h1" sx={{ ...landingType.h2, color: landing.textOnDark, fontSize: 'clamp(2rem,3.2vw,2.85rem)' }}>
              Your business.
              <br />
              <Box component="span" sx={gradientText}>One intelligent system.</Box>
            </Typography>
            <Typography sx={{ mt: 3, fontSize: '1.0625rem', lineHeight: 1.7, color: landing.textOnDarkMuted, maxWidth: 460 }}>
              Finance, operations, supply chain, sales and people — connected in one enterprise platform, writing to a single ledger that always agrees with itself.
            </Typography>

            {/* live module status */}
            <Box sx={{ mt: 5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {MODULES.map((m, i) => (
                <Box key={m} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 7, height: 7, borderRadius: '50%', backgroundColor: '#22c55e',
                      boxShadow: '0 0 0 0 rgba(34,197,94,0.4)',
                      animation: `nodeGlow 3s ease-in-out ${i * 0.4}s infinite`,
                      '@keyframes nodeGlow': { '0%,100%': { boxShadow: '0 0 0 0 rgba(34,197,94,0.4)' }, '50%': { boxShadow: '0 0 8px 2px rgba(34,197,94,0.25)' } },
                      ...reducedMotion,
                    }}
                  />
                  <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: landing.textOnDark }}>{m}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', letterSpacing: '0.08em', color: 'rgba(34,197,94,0.75)' }}>
                    ONLINE
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* RIGHT — login card */}
          <Box sx={{ ...glassSurface, p: { xs: 2.5, sm: 4.5 }, minWidth: 0, width: '100%', maxWidth: 440, mx: { xs: 'auto', md: 0 } }}>
            {/* mobile brand */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mb: 3 }}>
              <Wordmark size={34} tone="dark" gradient />
            </Box>

            {/* system status pill */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 1.25, py: 0.5, borderRadius: 999, border: `1px solid ${system.color}44`, backgroundColor: `${system.color}14` }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: system.color, ...(system.glow ? { animation: 'blink 1s ease-in-out infinite', '@keyframes blink': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.3 } }, ...reducedMotion } : {}) }} />
                <Typography sx={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.14em', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: system.color }}>
                  {system.label}
                </Typography>
              </Box>
              {workspace && (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: 'rgba(226,232,240,0.6)' }}>
                  <Language sx={{ fontSize: 14 }} />
                  <Typography sx={{ fontSize: '0.75rem', fontFamily: 'ui-monospace, monospace' }}>{`${workspace}.${ROOT_DOMAIN}`}</Typography>
                </Box>
              )}
            </Box>

            <Typography component="h2" sx={{ fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.01em', color: landing.textOnDark }}>
              Sign in to Palbon
            </Typography>
            <Typography sx={{ mt: 1, mb: 3.5, fontSize: '0.9375rem', color: landing.textOnDarkMuted }}>
              {workspace ? 'Enter your credentials to continue to your workspace.' : 'Welcome back. Enter your credentials to continue.'}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2, backgroundColor: 'rgba(220,38,38,0.12)', color: '#fecaca', border: '1px solid rgba(220,38,38,0.35)', '& .MuiAlert-icon': { color: '#f87171' } }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                fullWidth required id="email" name="email" type="email" label="Work email"
                autoComplete="email" autoFocus value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setActive(true)} onBlur={() => setActive(false)}
                disabled={isLoading} sx={darkField}
              />
              <TextField
                fullWidth required id="password" name="password" label="Password"
                type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setActive(true)} onBlur={() => setActive(false)}
                disabled={isLoading}
                sx={darkField}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton aria-label={showPassword ? 'Hide password' : 'Show password'} onClick={() => setShowPassword((v) => !v)} edge="end" size="small">
                        {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5, mb: 3 }}>
                <FormControlLabel
                  control={<Checkbox size="small" checked={remember} onChange={(e) => setRemember(e.target.checked)} sx={{ color: 'rgba(226,232,240,0.4)', '&.Mui-checked': { color: landing.accent } }} />}
                  label={<Typography sx={{ fontSize: '0.8125rem', color: landing.textOnDarkMuted }}>Remember me</Typography>}
                />
                <MuiLink href="#" underline="hover" sx={{ fontSize: '0.8125rem', color: landing.accent, '&:focus-visible': { outline: `2px solid ${landing.accent}`, outlineOffset: '2px', borderRadius: '2px' } }}>
                  Forgot password?
                </MuiLink>
              </Box>

              <Button
                type="submit" fullWidth disabled={isLoading || !ready}
                endIcon={!isLoading ? <ArrowForward /> : undefined}
                sx={{
                  height: 50, fontSize: '0.9375rem', fontWeight: 700, letterSpacing: '0.01em', borderRadius: 2.5,
                  color: '#05060f', background: landing.accentGradient, backgroundSize: '200% auto',
                  boxShadow: '0 8px 24px rgba(56,189,248,0.28)',
                  transition: 'background-position .4s ease, box-shadow .2s ease, transform .1s ease',
                  '&:hover': { backgroundPosition: 'right center', boxShadow: '0 10px 30px rgba(56,189,248,0.4)' },
                  '&:active': { transform: 'translateY(1px)' },
                  '&.Mui-disabled': { background: 'rgba(255,255,255,0.08)', color: 'rgba(226,232,240,0.35)', boxShadow: 'none' },
                }}
              >
                {isLoading ? (
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={18} sx={{ color: '#05060f' }} /> Authenticating…
                  </Box>
                ) : (
                  'Sign in to Palbon'
                )}
              </Button>
            </Box>

            <Divider sx={{ my: 3, '&::before, &::after': { borderColor: 'rgba(255,255,255,0.1)' } }}>
              <Typography sx={{ fontSize: '0.6875rem', letterSpacing: '0.1em', color: 'rgba(226,232,240,0.45)', px: 1 }}>OR</Typography>
            </Divider>

            <Button
              component={RouterLink} to="/sso-login" fullWidth variant="outlined"
              sx={{ height: 46, fontWeight: 600, borderRadius: 2.5, color: landing.textOnDark, borderColor: 'rgba(255,255,255,0.16)', '&:hover': { borderColor: 'rgba(255,255,255,0.34)', backgroundColor: 'rgba(255,255,255,0.03)' } }}
            >
              Continue with SSO
            </Button>

            <Typography sx={{ mt: 3.5, textAlign: 'center', fontSize: '0.8125rem', color: landing.textOnDarkMuted }}>
              {workspace ? (
                <>Not your workspace?{' '}<MuiLink href={`https://${ROOT_DOMAIN}`} underline="hover" sx={{ color: landing.accent }}>Find the right one</MuiLink></>
              ) : (
                <>New to Palbon?{' '}<MuiLink component={RouterLink} to="/signup" underline="hover" sx={{ color: landing.accent }}>Create a workspace</MuiLink></>
              )}
            </Typography>
          </Box>
        </Box>

        {/* footer wordmark on mobile-less composition */}
        <Typography sx={{ position: 'absolute', bottom: 16, left: 0, right: 0, textAlign: 'center', fontSize: '0.6875rem', letterSpacing: '0.14em', color: 'rgba(226,232,240,0.35)', fontFamily: 'ui-monospace, monospace' }}>
          PALBON · ENTERPRISE BUSINESS PLATFORM
        </Typography>
      </Box>
    </MuiThemeProvider>
  );
};

export default LoginPage;
